import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Minus, Trash2, ShoppingBag, Search, ChevronRight } from "lucide-react";
import ItemSearchPicker from "../../components/ItemSearchPicker";
import OrderReviewDialog from "../../components/OrderReviewDialog";
import { toast, Toaster } from "sonner";

const TYPES = ["doctor", "distributor", "retailer", "other"];

export default function MRPlaceOrder() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [cart, setCart] = useState([]);
    const [obo, setObo] = useState({ customer_master_id: "", name: "", phone: "", customer_type: "doctor" });
    const [address, setAddress] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
    const [customers, setCustomers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/products", { params: { q: q || undefined, active_only: false } });
        setProducts(data);
    };
    const loadCustomers = async () => {
        try {
            const { data } = await api.get("/customers");
            setCustomers(data);
        } catch (e) {
            // Customer master is accounts-only — MRs without the capability get 403.
            console.debug("Customer master not available for this role:", e.response?.status);
        }
    };
    useEffect(() => { load(); loadCustomers(); /* eslint-disable-next-line */ }, []);

    const pickCustomer = (id) => {
        const c = customers.find(x => x.id === id);
        if (!c) return setObo({ customer_master_id: "", name: "", phone: "", customer_type: "doctor" });
        setObo({ customer_master_id: c.id, name: c.name, phone: c.phone || "", customer_type: c.customer_type });
        setAddress(a => ({ ...a, name: c.name, phone: c.phone || "", line1: c.address_line1 || "", line2: c.address_line2 || "", city: c.city || "", state: c.state || "", pincode: c.pincode || "" }));
    };

    /** Reprice the entire cart whenever the customer_type changes (distributor pricing toggles). */
    useEffect(() => {
        if (cart.length === 0 || products.length === 0) return;
        setCart(cur => cur.map(line => {
            const prod = products.find(p => p.id === line.product_id);
            const variant = prod?.variants?.find(v => v.size_id === line.size_id);
            if (!variant) return line;
            const newPrice = obo.customer_type === "distributor"
                ? (variant.distributor_price || variant.price)
                : variant.price;
            return newPrice === line.price ? line : { ...line, price: newPrice };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obo.customer_type, products]);

    /** Save the current address as a new customer master entry (if not already linked).
     *  Returns the new customer id. */
    const saveAsNewCustomer = async () => {
        if (!obo.name && !address.name) return toast.error("Type the customer's name first");
        try {
            const payload = {
                name: obo.name || address.name,
                phone: obo.phone || address.phone || "",
                customer_type: obo.customer_type || "retailer",
                address_line1: address.line1 || "",
                address_line2: address.line2 || "",
                city: address.city || "",
                state: address.state || "",
                pincode: address.pincode || "",
            };
            const { data } = await api.post("/customers", payload);
            await loadCustomers();
            setObo(o => ({ ...o, customer_master_id: data.id }));
            toast.success(`Saved "${data.name}" to your customer master`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save customer");
        }
    };

    const addToCart = (p, v) => {
        setCart(cur => {
            const idx = cur.findIndex(x => x.product_id === p.id && x.size_id === v.size_id);
            if (idx >= 0) { const c = [...cur]; c[idx] = { ...c[idx], quantity: c[idx].quantity + 1 }; return c; }
            const price = obo.customer_type === "distributor" ? (v.distributor_price || v.price) : v.price;
            return [...cur, { product_id: p.id, size_id: v.size_id, name: p.name, size_label: v.size_label, price, quantity: 1 }];
        });
    };

    /** Add to cart from the unified ItemSearchPicker. Skips items that aren't linked to a real catalog product (orders need real product_id + size_id for stock deduction). */
    const addFromSearch = (picked) => {
        if (!picked.product_id || !picked.size_id) {
            toast.error(`"${picked.name}" is a price-list-only item. Add it to the catalog first before ordering.`);
            return;
        }
        setCart((cur) => {
            const idx = cur.findIndex((x) => x.product_id === picked.product_id && x.size_id === picked.size_id);
            if (idx >= 0) {
                const c = [...cur]; c[idx] = { ...c[idx], quantity: c[idx].quantity + 1 }; return c;
            }
            const price = obo.customer_type === "distributor" && picked.distributor_price != null
                ? picked.distributor_price
                : (picked.mrp != null ? picked.mrp : picked.distributor_price || picked.rate || 0);
            return [...cur, {
                product_id: picked.product_id, size_id: picked.size_id,
                name: picked.name, size_label: picked.size_label,
                price, quantity: 1,
            }];
        });
        toast.success(`Added ${picked.name} ${picked.size_label}`);
    };
    const updateQty = (i, d) => setCart(cur => cur.map((x, idx) => idx === i ? { ...x, quantity: Math.max(0, x.quantity + d) } : x).filter(x => x.quantity > 0));
    const setQty = (i, n) => setCart(cur => cur.map((x, idx) => idx === i ? { ...x, quantity: Math.max(1, parseInt(n, 10) || 1) } : x));
    const remove = (i) => setCart(cur => cur.filter((_, idx) => idx !== i));
    const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);

    const place = async () => {
        if (cart.length === 0) return toast.error("Cart is empty");

        // Auto-sync OBO ↔ address. The MR may have filled the customer name only
        // in the Shipping-Address panel inside the Order Review dialog; the OBO
        // section upstream may be empty. They're meant to refer to the SAME person.
        let effectiveObo = { ...obo };
        let effectiveAddress = { ...address };
        if (!effectiveObo.name && effectiveAddress.name) effectiveObo.name = effectiveAddress.name;
        if (!effectiveAddress.name && effectiveObo.name) effectiveAddress.name = effectiveObo.name;
        if (!effectiveObo.phone && effectiveAddress.phone) effectiveObo.phone = effectiveAddress.phone;
        if (!effectiveAddress.phone && effectiveObo.phone) effectiveAddress.phone = effectiveObo.phone;

        if (!effectiveObo.name) return toast.error("Enter the customer's name (in the Shipping Address or On Behalf Of panel)");
        // Relaxed validation — MRs in the field rarely have full addresses on hand.
        // Only require a locality (city OR line1).
        if (!effectiveAddress.city && !effectiveAddress.line1) {
            return toast.error("Please enter at least a locality (City or Address line 1)");
        }
        // Persist the sync back so the UI reflects what was sent
        setObo(effectiveObo);
        setAddress(effectiveAddress);

        setBusy(true);
        try {
            await api.post("/mr/orders", {
                order: {
                    items: cart.map(c => ({ product_id: c.product_id, size_id: c.size_id, quantity: c.quantity })),
                    address: effectiveAddress,
                    payment_method: "cod",
                    customer_master_id: effectiveObo.customer_master_id || null,
                },
                on_behalf_of: effectiveObo,
            });
            toast.success("Order placed on behalf of " + effectiveObo.name);
            setCart([]);
            setReviewOpen(false);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="mr-place-order" className="space-y-4">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Field operations</div>
            <h1 className="font-serif text-4xl text-stone-900">Place order on behalf</h1>

            <div className="bg-white border border-stone-200 p-4 space-y-3">
                <div className="label-eyebrow text-stone-500">On behalf of</div>
                <div className="grid sm:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Pick from master</Label>
                        <Select value={obo.customer_master_id || "none"} onValueChange={(v) => pickCustomer(v === "none" ? "" : v)}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="mr-customer-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                            <SelectContent><SelectItem value="none">— New / not in master —</SelectItem>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.customer_type})</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-stone-500">Type</Label>
                        <Select value={obo.customer_type} onValueChange={(v) => setObo(o => ({ ...o, customer_type: v }))}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="mr-customer-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-widest text-stone-500">Name *</Label><Input value={obo.name} onChange={(e) => setObo(o => ({ ...o, name: e.target.value }))} className="rounded-none border-stone-300 mt-1" data-testid="mr-obo-name" /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-stone-500">Phone</Label><Input value={obo.phone} onChange={(e) => setObo(o => ({ ...o, phone: e.target.value }))} className="rounded-none border-stone-300 mt-1" /></div>
                </div>
            </div>

            <div className="bg-white border border-stone-200 p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Quick add (searches catalog + price list)</div>
                <ItemSearchPicker
                    onPick={(picked) => addFromSearch({ ...picked, rate: picked.rate })}
                    preferDistributorPrice={obo.customer_type === "distributor"}
                    placeholder="Type medicine name, size or SKU…"
                    testId="mr-quick-search"
                />
                <details>
                    <summary className="text-[10px] uppercase tracking-widest text-stone-500 cursor-pointer">Or browse the catalog</summary>
                    <div className="mt-2 flex gap-2 items-center border-t border-stone-100 pt-2">
                        <Search className="w-4 h-4 text-stone-400 ml-2" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="border-0 rounded-none focus-visible:ring-0" data-testid="mr-search" />
                        <Button onClick={load} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Search</Button>
                    </div>
                </details>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3 max-h-[60vh] overflow-auto">
                    {products.map(p => (
                        <div key={p.id} className="bg-white border border-stone-200 p-3" data-testid={`mr-prod-${p.slug}`}>
                            <div className="font-serif text-base text-slate-900">{p.name}</div>
                            {p.indications && <div className="text-[11px] text-stone-500 line-clamp-1 mt-0.5">{p.indications}</div>}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
                                {p.variants?.map(v => (
                                    <button
                                        key={v.size_id}
                                        onClick={() => addToCart(p, v)}
                                        className="border border-stone-200 p-2 text-left hover:border-sky-700 hover:bg-sky-50/40 transition-colors group"
                                        data-testid={`mr-add-${p.slug}-${v.size_label}`}
                                    >
                                        <div className="font-mono text-xs text-stone-600">{v.size_label}</div>
                                        <div className="text-sky-700 text-base font-medium font-mono">{formatINR(obo.customer_type === "distributor" ? (v.distributor_price || v.price) : v.price)}</div>
                                        <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-0.5 group-hover:text-sky-700">Tap to add</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="bg-white border border-stone-200 sticky top-4 self-start">
                    <div className="p-4 border-b border-stone-100 bg-sky-50/40">
                        <div className="label-eyebrow text-sky-700">Order summary</div>
                        <div className="font-serif text-2xl mt-1 text-slate-900">{cart.length === 0 ? "Empty" : `${cart.length} item${cart.length === 1 ? "" : "s"}`}</div>
                        {cart.length > 0 && <div className="text-xs text-stone-500 mt-1">{cart.reduce((n, x) => n + x.quantity, 0)} units · {formatINR(subtotal)}</div>}
                    </div>
                    <div className="max-h-[460px] overflow-auto">
                        {cart.length === 0 ? (
                            <div className="p-8 text-center text-sm text-stone-500">
                                Use "Quick add" above or tap a price below to start the order.
                            </div>
                        ) : cart.map((c, i) => (
                            <div key={`${c.product_id}-${c.size_id}`} className="p-3 border-b border-stone-100 last:border-b-0" data-testid={`mr-cart-row-${i}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-900 line-clamp-2">{c.name}</div>
                                        <div className="text-[11px] text-stone-500 mt-0.5">{c.size_label} · {formatINR(c.price)} <span className="text-stone-400">each</span></div>
                                    </div>
                                    <button onClick={() => remove(i)} className="text-stone-400 hover:text-red-700 p-1 -m-1" title="Remove" data-testid={`mr-cart-remove-${i}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => updateQty(i, -1)}
                                            className="w-9 h-11 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                            data-testid={`mr-qty-minus-${i}`}
                                            aria-label="Decrease quantity"
                                        >
                                            <Minus className="w-4 h-4 text-stone-600" />
                                        </button>
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            min={1}
                                            value={c.quantity}
                                            onChange={(e) => setQty(i, e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            onBlur={(e) => { if (!e.target.value || parseInt(e.target.value, 10) < 1) setQty(i, 1); }}
                                            className="w-20 h-11 text-center text-lg font-semibold font-mono border-2 border-amber-400 focus:border-sky-700 focus:outline-none bg-amber-50 focus:bg-white"
                                            data-testid={`mr-qty-${i}`}
                                            aria-label={`Quantity for ${c.name}`}
                                        />
                                        <button
                                            onClick={() => updateQty(i, 1)}
                                            className="w-9 h-11 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                            data-testid={`mr-qty-plus-${i}`}
                                            aria-label="Increase quantity"
                                        >
                                            <Plus className="w-4 h-4 text-stone-600" />
                                        </button>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-stone-400">Line total</div>
                                        <div className="font-mono font-semibold text-sky-700 text-base" data-testid={`mr-line-total-${i}`}>{formatINR(c.price * c.quantity)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="p-4 border-t border-stone-100 text-sm flex justify-between bg-stone-50/50"><span className="text-stone-600">Subtotal</span><span className="font-semibold font-mono">{formatINR(subtotal)}</span></div>
                    <details className="px-4 py-2 border-t border-stone-100" open>
                        <summary className="text-xs uppercase tracking-widest text-stone-500 cursor-pointer">Shipping address</summary>
                        <p className="text-[11px] text-stone-500 mt-2 mb-2">
                            Only <strong>customer name</strong> and a <strong>locality (City <em>or</em> Line 1)</strong> are required. The rest is optional — the office will follow up if needed.
                        </p>
                        <div className="mt-1 space-y-2">
                            {[
                                ["name", "Customer name *"],
                                ["phone", "Phone (optional)"],
                                ["line1", "Address line 1 / Locality *"],
                                ["line2", "Line 2 (optional)"],
                                ["city", "City / Town *"],
                                ["state", "State (optional)"],
                                ["pincode", "Pincode (optional)"],
                            ].map(([k, l]) => (
                                <Input key={k} placeholder={l} value={address[k]} onChange={(e) => setAddress(a => ({ ...a, [k]: e.target.value }))} className="rounded-none border-stone-300 h-8" data-testid={`mr-addr-${k}`} />
                            ))}
                        </div>
                    </details>
                    <Button onClick={() => setReviewOpen(true)} disabled={busy || cart.length === 0} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full py-5" data-testid="mr-review-open">
                        <ShoppingBag className="w-4 h-4 mr-2" />Review &amp; place order
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                </aside>
            </div>

            <OrderReviewDialog
                open={reviewOpen}
                onOpenChange={setReviewOpen}
                cart={cart}
                setQty={setQty}
                updateQty={updateQty}
                removeItem={remove}
                address={address}
                setAddress={setAddress}
                addressFields={[
                    ["name", "Customer name *"],
                    ["phone", "Phone (optional)"],
                    ["line1", "Address line 1 / Locality *"],
                    ["line2", "Line 2 (optional)"],
                    ["city", "City / Town *"],
                    ["state", "State (optional)"],
                    ["pincode", "Pincode (optional)"],
                ]}
                addressNote="Only customer name + city OR line 1 are required"
                submitting={busy}
                onConfirm={place}
                customerLabel={obo.name ? `On behalf of: ${obo.name}${obo.customer_type ? ` (${obo.customer_type})` : ""}` : null}
                addressHeaderSlot={
                    <CustomerMasterPicker
                        value={obo.customer_master_id ? (customers.find(c => c.id === obo.customer_master_id) || { id: obo.customer_master_id, name: obo.name, phone: obo.phone }) : null}
                        onPick={(c) => pickCustomer(c?.id || "")}
                        onSaveAsNew={saveAsNewCustomer}
                        showTypeSelector
                        typeValue={obo.customer_type}
                        onTypeChange={(v) => setObo(o => ({ ...o, customer_type: v }))}
                        dataTestIdPrefix="mr-review-master"
                    />
                }
                testIdPrefix="mr"
            />
        </div>
    );
}
